import React, { forwardRef, useMemo, useState, useEffect } from 'react';
import QRCode from 'qrcode';
import type { Order, BusinessInfo, SubBill, PaymentItemAllocation } from '../../types';

/**
 * Preview sub-bill: same shape as a closed SubBill but without an id/paymentIds — used when the
 * cashier asks to print a customer's bill BEFORE taking payment.
 */
export interface SubBillPreview {
  name?: string;
  total: number;
  itemAllocations: PaymentItemAllocation[];
  discountAllocationMode: 'proportional' | 'remainder';
}

interface PrintableReceiptProps {
  order: Order;
  businessInfo?: BusinessInfo;
  /** When true, show "ORDER / BILL" instead of implying receipt (e.g. for presenting to customer before payment) */
  asOrderBill?: boolean;
  /** Render only this sub-bill's items + totals + payments. Use for itemized partial receipts. */
  subBill?: SubBill;
  /** Render a draft sub-bill receipt before payment (no payment block, header says "ORDER / BILL"). */
  subBillPreview?: SubBillPreview;
}

const PrintableReceipt = forwardRef<HTMLDivElement, PrintableReceiptProps>(({ order, businessInfo, asOrderBill, subBill, subBillPreview }, ref) => {
  const { appliedReward } = order;
  // Sub-bill preview is, by definition, an unpaid bill.
  const isSubBill = !!subBill || !!subBillPreview;
  const showAsOrderBill = asOrderBill ?? (subBillPreview ? true : order.paymentStatus !== 'paid');

  // Use stored totals only for Uber/DoorDash (platform %); for others compute from items so receipt is correct after item add/remove
  const {
    subtotal,
    taxAmount,
    totalAmount,
    discountAmount,
    totalBeforeDiscount,
    rewardDiscount,
    firstTableDiscount,
    manualDiscount,
    couponDiscount,
    residentDiscount,
    deliveryChargeAmount,
  } = useMemo(() => {
    const useStored =
      order.orderType === 'uber-doordash' &&
      order.totalAmount != null &&
      order.subtotal != null &&
      order.taxAmount != null &&
      !Number.isNaN(order.totalAmount) &&
      order.totalAmount >= 0;
    if (useStored) {
      const items = order.items ?? [];
      const totalBeforeDiscount = items.reduce(
        (sum, item) => (item.removed ? sum : sum + item.price * item.quantity),
        0
      );
      const rewardDiscount = order.appliedReward ? order.appliedReward.discountValue : 0;
      const method = order.discountMethod ?? 'manual';
      const manualDiscount = method === 'manual' ? order.manualDiscount ?? 0 : 0;
      const firstTableDiscount = method === 'first-table' ? Math.max(0, order.firstTableDiscountAmount ?? 0) : 0;
      const couponDiscount = order.couponDiscountAmount ?? 0;
      const residentDiscount = order.residentDiscountAmount ?? 0;
      return {
        subtotal: order.subtotal,
        taxAmount: order.taxAmount,
        totalAmount: order.totalAmount,
        discountAmount: rewardDiscount + manualDiscount + firstTableDiscount + couponDiscount + residentDiscount,
        totalBeforeDiscount,
        rewardDiscount,
        firstTableDiscount,
        manualDiscount,
        couponDiscount,
        residentDiscount,
        deliveryChargeAmount: 0,
      };
    }
    const items = order.items ?? [];
    const totalBeforeDiscount = items.reduce(
      (sum, item) => (item.removed ? sum : sum + item.price * item.quantity),
      0
    );
    const rewardDiscount = order.appliedReward ? order.appliedReward.discountValue : 0;
    const method = order.discountMethod ?? 'manual';
    const manualDiscount = method === 'manual' ? order.manualDiscount ?? 0 : 0;
    const firstTableDiscount = method === 'first-table' ? Math.max(0, order.firstTableDiscountAmount ?? 0) : 0;
    const couponDiscount = order.couponDiscountAmount ?? 0;
    const residentDiscount = order.residentDiscountAmount ?? 0;
    const totalAfterItemDiscounts =
      totalBeforeDiscount - rewardDiscount - manualDiscount - firstTableDiscount - couponDiscount - residentDiscount;
    const deliveryChargeAmount =
      order.orderType === 'delivery' ? Math.max(0, order.deliveryCharge ?? 0) : 0;
    const totalAfterDiscounts = totalAfterItemDiscounts + deliveryChargeAmount;
    const tax = totalAfterDiscounts * (15 / 115);
    const sub = totalAfterDiscounts - tax;
      return {
        subtotal: sub,
        taxAmount: tax,
        totalAmount: totalAfterDiscounts,
        discountAmount: rewardDiscount + manualDiscount + firstTableDiscount + couponDiscount + residentDiscount,
      totalBeforeDiscount,
      rewardDiscount,
      firstTableDiscount,
      manualDiscount,
      couponDiscount,
      residentDiscount,
      deliveryChargeAmount,
      };
  }, [
    order.orderType,
    order.items,
    order.appliedReward,
    order.manualDiscount,
    order.discountMethod,
    order.firstTableDiscountAmount,
    order.couponDiscountAmount,
    order.residentDiscountAmount,
    order.deliveryCharge,
    order.totalAmount,
    order.subtotal,
    order.taxAmount,
  ]);

  const isDiscountItem = (item: (typeof order.items)[0]) =>
    (typeof item.price === 'number' && item.price < 0) || String(item.id).includes('combo-discount');

  // Customer receipt: only current items (no batches, no removed, no NEW markers)
  const itemsForReceipt = useMemo(() => {
    return (order.items ?? []).filter((i) => !i.removed && !isDiscountItem(i));
  }, [order.items]);

  // Sub-bill view (closed sub-bill OR pre-payment preview): build a focused snapshot of items,
  // totals and payments specific to this customer's slice.
  const subBillSlice = useMemo(() => {
    if (!subBill && !subBillPreview) return null;
    const allocations = (subBill?.itemAllocations ?? subBillPreview?.itemAllocations) ?? [];
    const itemsMap = new Map<string, (typeof order.items)[0]>();
    (order.items ?? []).forEach((it, idx) => {
      const lineId = it.lineId ?? `idx-${idx}`;
      itemsMap.set(lineId, it);
    });
    const rows = allocations.map((a) => {
      const it = itemsMap.get(a.lineId);
      const name = it?.name ?? 'Item';
      const meatOption = it?.selectedMeatOption;
      return {
        lineId: a.lineId,
        name,
        quantity: a.quantity,
        meatOption,
        lineSubtotal: a.lineSubtotal,
        allocatedDiscount: a.allocatedDiscount,
        allocatedDelivery: a.allocatedDelivery,
        allocatedTotal: a.allocatedTotal,
      };
    });
    const itemsSubtotal = rows.reduce((s, r) => s + r.lineSubtotal, 0);
    const allocatedDiscount = rows.reduce((s, r) => s + r.allocatedDiscount, 0);
    const allocatedDelivery = rows.reduce((s, r) => s + r.allocatedDelivery, 0);
    const total = rows.reduce((s, r) => s + r.allocatedTotal, 0);
    const tax = total * (15 / 115);
    const sub = total - tax;
    const sbPayments = subBill
      ? (order.payments ?? []).filter((p) => p.id && subBill.paymentIds.includes(p.id))
      : [];
    return {
      name: subBill?.name ?? subBillPreview?.name,
      mode: subBill?.discountAllocationMode ?? subBillPreview?.discountAllocationMode ?? 'proportional',
      rows,
      itemsSubtotal,
      allocatedDiscount,
      allocatedDelivery,
      total,
      gst: tax,
      net: sub,
      payments: sbPayments,
      isPreview: !!subBillPreview && !subBill,
    };
  }, [subBill, subBillPreview, order.items, order.payments]);

  // Generate QR code as data URL so it shows and prints reliably (no external image load)
  const [reviewQrDataUrl, setReviewQrDataUrl] = useState<string | null>(null);
  const reviewUrl = businessInfo?.googleReviewUrl;
  useEffect(() => {
    if (!reviewUrl || typeof reviewUrl !== 'string') {
      setReviewQrDataUrl(null);
      return;
    }
    let cancelled = false;
    QRCode.toDataURL(reviewUrl, { width: 96, margin: 1 })
      .then((url) => {
        if (!cancelled) setReviewQrDataUrl(url);
      })
      .catch(() => {
        if (!cancelled) setReviewQrDataUrl(null);
      });
    return () => { cancelled = true; };
  }, [reviewUrl]);

  // Default business info if not provided
  const business = businessInfo || {
    businessName: 'KINGS PARK',
    address: '123 Restaurant Ave, Food City',
    gstNumber: '123-456-789',
  };

  let orderIdentifier: React.ReactNode;
  if (order.orderType === 'takeaway') {
    orderIdentifier = `Takeaway: TAKEOUT-${order.takeawayNumber}`;
  } else if (order.orderType === 'delivery') {
    const deliveryNum = `DELIVERY-${order.deliveryNumber}`;
    if (order.deliveryInfo) {
      orderIdentifier = `Delivery: ${order.deliveryInfo.platform} (${deliveryNum})`;
    } else {
      orderIdentifier = `Delivery: ${deliveryNum}`;
    }
  } else {
    const tableLabel = order.tableName || `Table ${order.tableNumber || order.tableId}`;
    orderIdentifier = (
      <span>
        Dine-in: <strong className="font-bold text-lg text-black uppercase print:text-xl">{tableLabel}</strong>
      </span>
    );
  }

  // ---- Sub-bill receipt (focused customer slice) -------------------------------------------
  if (isSubBill && subBillSlice) {
    return (
      <div ref={ref} className="printable-receipt">
        <div className="receipt-content">
          <header className="text-center mb-4">
            <h1 className="text-2xl font-bold">{business.businessName}</h1>
            <p className="text-sm font-semibold uppercase tracking-wide mt-1 text-gray-700">
              {subBillSlice.isPreview ? 'Sub-bill / Order' : 'Sub-bill receipt'}
            </p>
            <p className="text-xs">{business.address}</p>
            <p className="text-xs">GST #{business.gstNumber}</p>
            {business.phone && <p className="text-xs">{business.phone}</p>}
          </header>
          <div className="text-xs mb-2">
            <p>Order ID: {order.id}</p>
            <p>Type: {orderIdentifier}</p>
            <p className="font-semibold">Sub-bill: {subBillSlice.name || 'Guest'}</p>
            <p className="text-[10px] text-gray-600">
              Discount allocation: {subBillSlice.mode === 'proportional' ? 'Proportional' : 'Apply to remainder'}
            </p>
          </div>
          <table className="w-full text-xs table-fixed">
            <colgroup>
              <col />
              <col className="w-20" />
            </colgroup>
            <thead>
              <tr className="border-b-2 border-dashed border-black">
                <th className="text-left pb-1">ITEM</th>
                <th className="text-right pb-1 whitespace-nowrap">PRICE</th>
              </tr>
            </thead>
            <tbody>
              {subBillSlice.rows.map((r) => {
                const itemLabel = `${r.quantity}x ${r.name}${r.meatOption ? ` (${r.meatOption})` : ''}`;
                const shouldWrap = itemLabel.length > 32;
                return (
                  <tr key={r.lineId}>
                    <td className={`pr-2 ${shouldWrap ? '' : 'whitespace-nowrap'}`}>{itemLabel}</td>
                    <td className="text-right whitespace-nowrap align-top">${r.lineSubtotal.toFixed(2)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="border-t-2 border-dashed border-black mt-2 pt-2 text-xs">
            <p className="flex justify-between"><span>Items subtotal:</span> <span>${subBillSlice.itemsSubtotal.toFixed(2)}</span></p>
            {subBillSlice.allocatedDiscount > 0 && (
              <p className="flex justify-between"><span>Discount share:</span> <span>-${subBillSlice.allocatedDiscount.toFixed(2)}</span></p>
            )}
            {subBillSlice.allocatedDelivery > 0 && (
              <p className="flex justify-between"><span>Delivery share:</span> <span>+${subBillSlice.allocatedDelivery.toFixed(2)}</span></p>
            )}
            <p className="flex justify-between"><span>Subtotal (ex. GST):</span> <span>${subBillSlice.net.toFixed(2)}</span></p>
            <p className="flex justify-between"><span>GST (15%):</span> <span>${subBillSlice.gst.toFixed(2)}</span></p>
            <p className="font-bold text-sm flex justify-between mt-1"><span>SUB-BILL TOTAL:</span> <span>${subBillSlice.total.toFixed(2)}</span></p>
          </div>
          {subBillSlice.payments.length > 0 && (
            <div className="border-t-2 border-dashed border-black mt-2 pt-2 text-xs">
              <p className="font-semibold mb-1">Payment(s) for this sub-bill:</p>
              {subBillSlice.payments.map((payment, idx) => {
                const method = (payment.method || '').toString().toLowerCase();
                const label = method.startsWith('card')
                  ? 'CARD'
                  : method === 'account2account'
                    ? 'BANK TRANSFER'
                    : method === 'cash'
                      ? 'CASH'
                      : (method ? method.replace(/_/g, ' ').toUpperCase() : 'PAYMENT');
                return (
                  <p key={payment.id ?? idx} className="flex justify-between">
                    <span className="capitalize">{label}</span>
                    <span className="ml-2 flex-1 text-right">${payment.amount.toFixed(2)}</span>
                  </p>
                );
              })}
            </div>
          )}
          {subBillSlice.isPreview && (
            <div className="border-t-2 border-dashed border-black mt-2 pt-2 text-xs">
              <p className="text-center font-semibold">** NOT YET PAID **</p>
            </div>
          )}
          <footer className="text-center mt-4 text-xs">
            <p>Thank you for dining with us!</p>
            <p className="text-[10px] mt-1 text-gray-600">This receipt covers a portion of order {order.id}.</p>
          </footer>
        </div>
      </div>
    );
  }

  return (
    <div ref={ref} className="printable-receipt">
      <div className="receipt-content">
        <header className="text-center mb-4">
          <h1 className="text-2xl font-bold">{business.businessName}</h1>
          {showAsOrderBill && (
            <p className="text-sm font-semibold uppercase tracking-wide mt-1 text-gray-700">Order / Bill</p>
          )}
          <p className="text-xs">{business.address}</p>
          <p className="text-xs">GST #{business.gstNumber}</p>
          {business.phone && <p className="text-xs">{business.phone}</p>}
        </header>
        <div className="text-xs mb-2">
          <p>Order ID: {order.id}</p>
          <p>Type: {orderIdentifier}</p>
          {(order.orderType === 'takeaway' || order.orderType === 'delivery') && order.customerName && (
            <p className="font-semibold">Customer: {order.customerName}</p>
          )}
          <p>Date: {(() => {
            let orderDate: Date;
            if (typeof order.timestamp === 'number') {
              orderDate = new Date(order.timestamp);
            } else if (order.timestamp instanceof Date) {
              orderDate = order.timestamp;
            } else if (order.timestamp && typeof order.timestamp === 'object' && 'seconds' in order.timestamp) {
              orderDate = new Date((order.timestamp as any).seconds * 1000);
            } else {
              return 'Invalid Date';
            }
            if (isNaN(orderDate.getTime())) {
              return 'Invalid Date';
            }
            return orderDate.toLocaleString('en-NZ', {
              timeZone: 'Pacific/Auckland',
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12: true
            });
          })()}</p>
        </div>
        {order.notes && (
          <div className="text-xs mb-2 p-2 bg-amber-50 border border-amber-200 rounded break-words">
            <p className="font-medium text-amber-900">Order note:</p>
            <p className="text-amber-800 break-words">{order.notes}</p>
          </div>
        )}
        <table className="w-full text-xs table-fixed">
          <colgroup>
            <col />
            <col className="w-20" />
          </colgroup>
          <thead>
            <tr className="border-b-2 border-dashed border-black">
              <th className="text-left pb-1">ITEM</th>
              <th className="text-right pb-1 whitespace-nowrap">PRICE</th>
            </tr>
          </thead>
          <tbody>
            {itemsForReceipt.map((item, idx) => {
              const key = item.lineId ?? `item-${idx}-${item.id}`;
              const itemLabel = `${item.quantity}x ${item.name}${item.selectedMeatOption ? ` (${item.selectedMeatOption})` : ''}`;
              // Keep short labels on one line; allow wrapping only for genuinely long names.
              const shouldWrapItemLabel = itemLabel.length > 32;
              return (
                <React.Fragment key={key}>
                  <tr>
                    <td className={`pr-2 ${shouldWrapItemLabel ? '' : 'whitespace-nowrap'}`}>
                      {itemLabel}
                    </td>
                    <td className="text-right whitespace-nowrap align-top">${(item.price * item.quantity).toFixed(2)}</td>
                  </tr>
                  {item.notes && (
                    <tr>
                      <td colSpan={2} className="text-[10px] text-gray-700 pb-0.5 pl-1 break-words">
                        <span className="font-medium">Note:</span> {item.notes}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        <div className="border-t-2 border-dashed border-black mt-2 pt-2 text-xs">
          <p className="flex justify-between"><span>Items Total:</span> <span>${totalBeforeDiscount.toFixed(2)}</span></p>
          {appliedReward && rewardDiscount > 0 && (
            <p className="flex justify-between">
              <span>Discount ({appliedReward.name}):</span>
              <span>-${rewardDiscount.toFixed(2)}</span>
            </p>
          )}
          {firstTableDiscount > 0 && (
            <p className="flex justify-between">
              <span>First Table Discount:</span>
              <span>-${firstTableDiscount.toFixed(2)}</span>
            </p>
          )}
          {manualDiscount > 0 && (
            <p className="flex justify-between">
              <span>Manual Discount:</span>
              <span>-${manualDiscount.toFixed(2)}</span>
            </p>
          )}
          {couponDiscount > 0 && (
            <p className="flex justify-between">
              <span>
                {order.couponUnitLabel
                  ? `Building discount — ${order.couponUnitLabel}`
                  : 'Coupon'} ({order.couponCode || 'CODE'}):
              </span>
              <span>-${couponDiscount.toFixed(2)}</span>
            </p>
          )}
          {residentDiscount > 0 && (
            <p className="flex justify-between">
              <span>Resident Discount:</span>
              <span>-${residentDiscount.toFixed(2)}</span>
            </p>
          )}
          {deliveryChargeAmount > 0 && (
            <p className="flex justify-between">
              <span>Delivery charge (incl. GST):</span>
              <span>+${deliveryChargeAmount.toFixed(2)}</span>
            </p>
          )}
          {discountAmount > 0 && (
            <p className="flex justify-between font-medium">
              <span>Total Discount:</span>
              <span>-${discountAmount.toFixed(2)}</span>
            </p>
          )}
          <p className="flex justify-between"><span>Subtotal (ex. GST):</span> <span>${subtotal.toFixed(2)}</span></p>
          <p className="flex justify-between"><span>GST (15%):</span> <span>${taxAmount.toFixed(2)}</span></p>
          <p className="font-bold text-sm flex justify-between mt-1"><span>TOTAL:</span> <span>${totalAmount.toFixed(2)}</span></p>
        </div>
        {order.payments && order.payments.length > 0 && (() => {
          const amountPaid = order.payments.reduce((sum, p) => sum + p.amount, 0);
          const balanceDue = totalAmount - amountPaid;
          const showBalance = balanceDue > 0 || Math.abs(balanceDue) > 0.005;
          return (
          <div className="border-t-2 border-dashed border-black mt-2 pt-2 text-xs">
            <p className="font-semibold mb-1">Previous payment(s):</p>
            {order.payments.map((payment, idx) => {
              const method = (payment.method || '').toString().toLowerCase();
              const label = method.startsWith('card')
                ? 'CARD - Main Terminal'
                : method === 'account2account'
                  ? 'BANK TRANSFER'
                  : method === 'cash'
                    ? 'CASH'
                    : method
                      ? method.replace(/_/g, ' ').toUpperCase()
                      : 'PAYMENT';
              return (
              <p key={idx} className="flex justify-between">
                <span className="capitalize">{label}</span>
                <span className="ml-2 flex-1 text-right">
                  ${payment.amount.toFixed(2)}{' '}
                  <span className="ml-2 text-[10px] text-gray-600">
                    {(() => {
                      const ts = typeof payment.timestamp === 'number'
                        ? new Date(payment.timestamp)
                        : new Date();
                      if (isNaN(ts.getTime())) return '';
                      return ts.toLocaleTimeString('en-NZ', {
                        timeZone: 'Pacific/Auckland',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                        hour12: true,
                      });
                    })()}
                  </span>
                </span>
              </p>
            )})}
            <p className="flex justify-between mt-1 font-medium">
              <span>Amount already paid:</span>
              <span>${amountPaid.toFixed(2)}</span>
            </p>
            {showBalance && (
              <p className="flex justify-between mt-1 font-bold text-sm">
                <span>Balance to pay:</span>
                <span>${Math.max(0, balanceDue).toFixed(2)}</span>
              </p>
            )}
          </div>
          );
        })()}
        {reviewUrl && reviewQrDataUrl && (
          <div className="text-center mt-4 pt-3 border-t border-dashed border-gray-300">
            <p className="text-xs font-medium text-gray-700 mb-1">We&apos;d love your feedback!</p>
            <p className="text-[10px] text-gray-600 mb-2">Scan to leave a Google review</p>
            <img
              src={reviewQrDataUrl}
              alt="Scan to leave a Google review"
              className="mx-auto w-24 h-24 print:w-20 print:h-20"
              width={96}
              height={96}
            />
          </div>
        )}
        <footer className="text-center mt-4 text-xs">
          <p>Thank you for dining with us!</p>
        </footer>
      </div>
    </div>
  );
});

export default PrintableReceipt;
